import { getConstants, getArticle, getArticlesPage } from '@lib/sanity';
import Head from 'next/head';

import { PageWrapper } from '../../components/wrapper';

import type { Article as TArticle, ArticlesPage, Constants } from '@lib/queries';
import type { GetServerSideProps } from 'next';

import { Article } from '~/components/article';
import { setAppContext } from '~/components/context';
import { CtaBlock } from '~/components/cta-block';

type Props = { article: TArticle; page: ArticlesPage; context: Constants };

const ArticlePage = ({ article, page, context }: Props) => {
  setAppContext(context);

  const { author, datePublished, snippet, title } = article;

  const articleSchema = {
    '@context': 'https://schema.org/',
    '@type': 'Article',
    author: {
      '@type': 'Person',
      name: author.name,
    },
    datePublished: datePublished,
    description: snippet,
    headline: title,
  };

  return (
    <PageWrapper>
      <Head>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Head>
      <Article {...article} />
      {page.cta && <CtaBlock {...page.cta} />}
    </PageWrapper>
  );
};

type Params = { slug: string };

export const getServerSideProps: GetServerSideProps<Props, Params> = async ({ params }) => {
  const article = await getArticle(params?.slug ?? '');
  const page = await getArticlesPage();
  const context = await getConstants();

  return {
    props: { article, page, context },
  };
};

export default ArticlePage;
